import React, { useEffect, useState } from 'react';

import { faClose } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from 'src/common/components/button/button';
import { Checkbox } from 'src/common/components/checkbox/checkbox';
import { Flex } from 'src/common/components/flex/flex';
import { Text } from 'src/common/components/text/text';

import './terms-2026-banner.scss';

export const TERMS_OF_SERVICE_URL =
  'https://streamable-support.zendesk.com/hc/en-us/articles/44623209368468-Terms-of-Service';
export const PRIVACY_POLICY_URL =
  'https://streamable-support.zendesk.com/hc/en-us/articles/44623215636372-Streamable-Privacy-Policy';

export const TERMS_2026_BANNER_START_DATE = new Date('2026-10-01T00:00:00Z');
export const TERMS_2026_BANNER_ENFORCEMENT_DATE = new Date(
  '2026-10-31T00:00:00Z'
);

export const isTerms2026BannerDismissible = (now: Date = new Date()) =>
  now < TERMS_2026_BANNER_ENFORCEMENT_DATE;

export const isTerms2026BannerWithinWindow = (now: Date) =>
  now >= TERMS_2026_BANNER_START_DATE;

const CLOCK_TICK_INTERVAL_MS = 60 * 1000;

type Terms2026BannerComponentProps = {
  isShowing: boolean;
  isAcceptPending: boolean;
  acceptError: string | null;
  onAccept: (marketingOptIn: boolean) => void;
  userKey?: string | number;
  now?: Date;
};

export const Terms2026BannerComponent = ({
  isShowing,
  isAcceptPending,
  acceptError,
  onAccept,
  userKey,
  now,
}: Terms2026BannerComponentProps) => {
  const [currentTime, setCurrentTime] = useState(() => now ?? new Date());
  const [isDismissed, setIsDismissed] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);

  useEffect(() => {
    if (now !== undefined) {
      setCurrentTime(now);
      return undefined;
    }

    const intervalId = setInterval(
      () => setCurrentTime(new Date()),
      CLOCK_TICK_INTERVAL_MS
    );

    return () => clearInterval(intervalId);
  }, [now]);

  useEffect(() => {
    setIsDismissed(false);
  }, [userKey]);

  const isDismissible = isTerms2026BannerDismissible(currentTime);

  if (!isShowing || !isTerms2026BannerWithinWindow(currentTime)) {
    return null;
  }

  if (isDismissed && isDismissible) {
    return null;
  }

  return (
    <div
      className="terms-2026-banner"
      role="region"
      aria-label="Legal terms update"
    >
      {isDismissible && (
        <Button
          onClick={() => setIsDismissed(true)}
          variant="icon"
          className="terms-2026-banner__close-btn"
        >
          <FontAwesomeIcon icon={faClose} size="lg" title="Dismiss" />
        </Button>
      )}

      <Flex direction="row" className="terms-2026-banner__content">
        <Flex direction="column" className="terms-2026-banner__text">
          <Text as="p">
            We're making some administrative changes and Streamable will soon be
            provided by Bending Spoons Operations S.p.A. We're also updating our{' '}
            <a
              href={TERMS_OF_SERVICE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Service
            </a>{' '}
            and{' '}
            <a
              href={PRIVACY_POLICY_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
            . These changes will take effect on November 1, 2026.
          </Text>
          <Text as="p">
            We've sent you an email with more information about the changes. By
            tapping &ldquo;Accept and Acknowledge&rdquo; below, or by continuing
            to use Streamable on or after November 1, 2026, you agree to the
            updated legal terms. If you don't agree to the updated legal terms,
            you'll need to stop using Streamable and delete your account before
            November 1, 2026.
          </Text>
        </Flex>

        <Flex direction="column" className="terms-2026-banner__actions">
          <Button
            onClick={() => onAccept(marketingOptIn)}
            color="primary"
            variant="filled"
            className="terms-2026-banner__accept-btn"
            disabled={isAcceptPending}
          >
            {isAcceptPending ? 'Saving...' : 'Accept and Acknowledge'}
          </Button>

          {acceptError && (
            <Text as="p" role="alert" className="terms-2026-banner__error">
              {acceptError}
            </Text>
          )}

          <Flex
            direction="row"
            alignItems="flex-start"
            className="terms-2026-banner__opt-in"
          >
            <Checkbox
              id="terms-2026-marketing-opt-in"
              checked={marketingOptIn}
              disabled={isAcceptPending}
              onChange={() => setMarketingOptIn((checked) => !checked)}
            />
            <label htmlFor="terms-2026-marketing-opt-in">
              <Text as="p">
                <strong>I'd like to stay in touch</strong>
                <br />
                By checking this box, you agree to be contacted by email for
                surveys and interviews, and to receive updates about our other
                products and services. You can unsubscribe at any time. For more
                details, see our{' '}
                <a
                  href={PRIVACY_POLICY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  privacy policy
                </a>
                .
              </Text>
            </label>
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
};
